/* eslint-disable @typescript-eslint/no-use-before-define */

import { useCallback } from 'react';

import { OutstandingAgreementType } from '__generated__/GQL';
import asBentoModule from 'common/bento/hoc/asBentoModule';
import withStateMachine from 'common/bento/hoc/withStateMachine/withStateMachine';
import assertNever from 'common/bento/lib/assertNever';
import stringifyState from 'common/bento/lib/stringifyState';
import Agreement from 'common/bento/modules/Agreement';
import { BentoModuleDoneStatus } from 'common/bento/types/BentoModule';
import NavigationContext from 'features/Onboarding/components/OBNavigation/NavigationContext';
import BeneficiariesDocs from 'features/Onboarding/legacy/Pages/BeneficiariesDocs';
import BeneficiariesDocsIntro from 'features/Onboarding/legacy/Pages/BeneficiariesDocsIntro';
import CannotOpenAccount from 'features/Onboarding/legacy/Pages/CannotOpenAccount';
import DownloadShineMessageSent from 'features/Onboarding/legacy/Pages/DownloadShineMessageSent';
import UnderReview from 'features/Onboarding/legacy/Pages/UnderReview';
import CompanyCompliance from 'features/Onboarding/modules/CompanyCompliance';
import logger from 'helpers/logger';

import OBLoaderPage from '../../components/OBLoaderPage/OBLoaderPage';
import OnboardingPageAdapter from '../../components/OnboardingPageAdapter';
import useOverrideSetCurrentPage from '../../hooks/useOverrideSetCurrentPage';
import { User } from '../../legacy/types';
import { OnboardingModuleData } from '../../machine/model';
import BusinessInfo from '../../modules/BusinessInfo';
import { State as BusinessInfoState } from '../../modules/BusinessInfo/machine';
import { State as PersonalInfoState } from '../../modules/PersonalInfo/machine';
import PersonalInfo from '../../modules/PersonalInfo/PersonalInfo';
import PricingPlan from '../../modules/PricingPlan';
import ResearchExperiment from '../../modules/ResearchExperiment';
import { State as ResearchExperimentState } from '../../modules/ResearchExperiment/machine';
import Verification from '../../modules/Verification';
import CheckIdentityDocument from '../../pages/CheckIdentityDocument';
import ResetOnboardingPrompt from '../../pages/ResetOnboardingPrompt';
import { ApplicantType, ApplicationType } from '../../types';
import machineConfig, { model, State } from './machine';

const logImpossibleEvent = (event: string, state: string) => {
  logger.error(`Impossible event '${event}' in state ${state}`);
};

interface OnboardingStandardProps {
  onboardingData: OnboardingModuleData;
}

const OnboardingStandard = asBentoModule<OnboardingStandardProps>(
  ({ onboardingData, onDone }) => {
    return (
      <OnboardingStandardComponent
        moduleData={{
          ...onboardingData,
        }}
        onDone={onDone}
      />
    );
  },
);

const OnboardingStandardComponent = withStateMachine<typeof model>(
  ({ machine: [state, send], moduleData }) => {
    const stringifiedState = stringifyState<State>(state);

    const adaptedPageProps = {
      onPrev: useCallback(() => send(model.events.PREV()), [send]),
      setCurrentPage: useOverrideSetCurrentPage(send),
      user: moduleData.user.profile as User,
    };

    const {
      companyData,
      user: { uid },
    } = moduleData;

    const getContent = () => {
      if (stringifiedState === State.BUSINESS_INFO) {
        return (
          <BusinessInfo
            companyProfileId={companyData?.company.companyProfileId}
            onDone={({ finalState, status }) => {
              if (finalState === BusinessInfoState.ABORT_TO_CAPITAL_DEPOSIT) {
                return send(model.events.GO_CAPITAL_DEPOSIT());
              }

              return status === BentoModuleDoneStatus.DONE
                ? send(model.events.NEXT())
                : send(model.events.PREV());
            }}
          />
        );
      }

      if (
        stringifiedState === State.DONE ||
        stringifiedState === State.ABORT ||
        stringifiedState === State.ABORT_APPLICATION ||
        stringifiedState === State.ABORT_TO_CAPITAL_DEPOSIT
      ) {
        return <OBLoaderPage onPrev={() => null} />;
      }

      if (!companyData) {
        throw new Error(
          `companyData should be defined when not in the BusinessInfo module (current state is ${stringifiedState})`,
        );
      }

      /**
       * Else, we should be in a migrated module.
       */
      switch (stringifiedState) {
        case State.ABORT_STANDARD_APPLICATION_PROMPT: {
          return (
            <ResetOnboardingPrompt
              company={companyData.company.profile}
              onAbort={() => send(model.events.ABORT_APPLICATION())}
              onPrev={() => send(model.events.PREV())}
              onResume={() => send(model.events.NEXT())}
            />
          );
        }

        case State.ONBOARDING_RESEARCH: {
          return (
            <ResearchExperiment
              onDone={({ finalState }) => {
                if (
                  finalState === ResearchExperimentState.ABORT_TO_RESET_PROMPT
                ) {
                  return send(model.events.GO_RESET_PROMPT());
                }

                return send(model.events.NEXT());
              }}
              uid={uid}
            />
          );
        }

        case State.PERSONAL_INFO: {
          return (
            <PersonalInfo
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.Standard}
              companyProfileId={companyData.company.companyProfileId}
              onDone={({ finalState }) => {
                if (finalState === PersonalInfoState.ABORT_TO_RESET_PROMPT) {
                  return send(model.events.GO_RESET_PROMPT());
                }

                return send(model.events.NEXT());
              }}
            />
          );
        }

        case State.COMPANY_COMPLIANCE: {
          return (
            <CompanyCompliance
              companyProfileId={companyData.company.companyProfileId}
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : send(model.events.PREV())
              }
            />
          );
        }

        case State.VERIFICATION: {
          return (
            <Verification
              applicantType={ApplicantType.AccountHolder}
              applicationType={ApplicationType.Standard}
              companyProfileId={companyData.company.companyProfileId}
              isMainApplicant
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.ABORT
                  ? send(model.events.ABORT_APPLICATION())
                  : send(model.events.NEXT())
              }
            />
          );
        }

        case State.AGREEMENT: {
          return (
            <Agreement
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : send(model.events.PREV())
              }
              type={OutstandingAgreementType.Cgu}
            />
          );
        }

        case State.PRICING_PLAN: {
          return (
            <PricingPlan
              onDone={({ status }) =>
                status === BentoModuleDoneStatus.DONE
                  ? send(model.events.NEXT())
                  : send(model.events.PREV())
              }
              packageType={null}
            />
          );
        }

        case State.REVIEW: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={UnderReview}
              {...adaptedPageProps}
            />
          );
        }

        case State.BENEFICIARIES_DOCS: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={BeneficiariesDocs}
              {...adaptedPageProps}
              setCurrentPage={(page) => {
                switch (page) {
                  case 'verification-home':
                    return send('PREV');

                  default: {
                    logImpossibleEvent(page, stringifiedState);
                    return null;
                  }
                }
              }}
            />
          );
        }

        case State.BENEFICIARIES_DOCS_INTRO: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={BeneficiariesDocsIntro}
              {...adaptedPageProps}
            />
          );
        }

        case State.CANNOT_OPEN_ACCOUNT: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={CannotOpenAccount}
              {...adaptedPageProps}
            />
          );
        }

        case State.DOWNLOAD_SHINE_MESSAGE_SENT: {
          return (
            <OnboardingPageAdapter
              LegacyOnboardingPage={DownloadShineMessageSent}
              {...adaptedPageProps}
            />
          );
        }

        case State.CHECK_IDENTITY_DOCUMENT:
          return (
            <CheckIdentityDocument
              onContinue={() => send(model.events.NEXT())}
              onPrev={() => send(model.events.PREV())}
            />
          );

        /**
         * All these except SEND_APPLICATION & INIT are already handled above, this is for TS.
         * @todo remove as we migrate these modules.
         */
        case State.SEND_APPLICATION:
        case State.USER_PROFILE_SEND:
        case State.INIT:
          return <OBLoaderPage onPrev={() => null} />;

        default:
          return assertNever(stringifiedState);
      }
    };

    return (
      <NavigationContext.Provider
        value={{
          steps: [],
        }}
      >
        {getContent()}
      </NavigationContext.Provider>
    );
  },

  {
    devTools: true,
    machineConfig,
  },
);

export default OnboardingStandard;
